// src/pages/ChatbotPage.tsx
import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useLocalization } from "../hooks/useLocalization";
import { useNavigate } from "react-router-dom";
import { HaloAnimation } from "../components/HaloAnimation";

/* ====== 外層：mobile-first，置中版心 ====== */
const Page = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100svh;
  width: 100%;
  margin: 0 auto;
  padding-top: 24px;
  z-index: 10; /* 蓋在 layout 背景上 */
`;

/* ====== 標題區 ====== */
const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 6px;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const TitleLine = styled.div`
  font-family: "PingFang TC", "SF Pro TC", "Noto Sans TC", system-ui, sans-serif;
  font-weight: 400;
  font-size: 26px;
  line-height: 1.35;
  text-align: center;
  letter-spacing: 1px;

  background: linear-gradient(
    90deg,
    #757ea7 -8.1%,
    #ffffff 53.63%,
    #3e466d 108.63%
  );
  -webkit-text-fill-color: transparent;
  background-clip: text;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    max-width: 90%;
    font-size: 42px;
  }
`;

/* ====== 中央球體圖片 ====== */
const HeroWrap = styled.div`
  display: flex;
  justify-content: center;
  width: min(450px, 85vw);
  height: auto;
`;

/* ====== 底部 Dock（依設計） ====== */
const BottomDock = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 38px;
  display: flex;
  justify-content: center;
  padding: 0 12px calc(env(safe-area-inset-bottom, 0px) + 8px);
  z-index: 30;
  pointer-events: none; /* 只讓內層按鈕吃事件 */
`;

const DockInner = styled.div`
  width: 100%;
  max-width: 1024px;
  padding: 0 8px 12px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: start; /* 讓我們自行用 padding 對齊圓心 */
  gap: 12px;
  pointer-events: none;

  /* 中間大圓的直徑（也就是最大直徑） */
  --center-circle: clamp(72px, 22vw, 144px);
  --max-circle: var(--center-circle);
`;

/* 中央大圓上傳鈕 + 主/副標 */
const CenterWrap = styled.div`
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

/* ========= Figma「上傳按鈕」轉 styled-components ========= */

/** 整組：中間大圓 + 標籤 */
const UploadGroup = styled.div`
  /* 告訴這一組：我的圓直徑就是最大值 */
  --circle-size: var(--max-circle);

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(4px, 1vw, 8px);
  pointer-events: auto;

  /* 中間這顆最大，不需要補償 */
  padding-top: 0;
`;

const UploadButtonFig = styled.button`
  position: relative;
  width: var(--circle-size);
  height: var(--circle-size);
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.08s ease, filter 0.15s ease;

  &:active {
    transform: translateY(1px);
  }
  &:hover {
    filter: brightness(1.05);
  }
`;

/** 外層圓 (98x98 → 等比例縮) */
const RingOuter = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(217, 217, 217, 0.069) 0%,
    rgba(255, 255, 255, 0.21) 100%
  );
  border: 0.5px solid rgba(255, 255, 255, 0.27);
`;

/** 中層圓 */
const RingMiddle = styled.div`
  position: absolute;
  top: 12.24%;
  left: 12.24%;
  width: 75.5%;
  height: 75.5%;
  border-radius: 50%;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(217, 217, 217, 0.115) 0%,
    rgba(255, 255, 255, 0.35) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

/** 內層圓 */
const RingInner = styled.div`
  position: absolute;
  top: 19.4%;
  left: 19.4%;
  width: 61.2%;
  height: 61.2%;
  border-radius: 50%;
  background: radial-gradient(
    50% 50% at 50% 50%,
    rgba(217, 217, 217, 0.23) 0%,
    rgba(255, 255, 255, 0.7) 100%
  );
  border: 1px solid #ffffff;
`;

/** 上箭頭 */
const ArrowUp = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 10%;
  filter: drop-shadow(0px 4px 9.9px rgba(0, 0, 0, 0.25));

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

/** 文字標籤 */
const UploadLabel = styled.div`
  text-align: center;
  text-wrap: wrap;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: clamp(12px, 3vw, 14px);
  line-height: 1.4;
  background: linear-gradient(0deg, #ffffff, #ffffff),
    linear-gradient(0deg, rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0.32)),
    linear-gradient(
      89.61deg,
      rgba(255, 255, 255, 0) -20.99%,
      #ffffff 38.34%,
      rgba(255, 255, 255, 0) 100%
    );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0px 4px 10.4px rgba(0, 0, 0, 0.25);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
  }
`;

/* ========= Figma：左右側小圓按鈕（45x45）＋標籤 ========= */

const MiniGroup = styled.div`
  /* 小圓直徑（Figma 45px，給個響應式上限） */
  --circle-size: clamp(40px, 12vw, 75px);

  pointer-events: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: clamp(4px, 1vw, 6px);

  /* ⭐ 圓心對齊的關鍵：把小圓往下推 (max - self) / 2 */
  padding-top: calc((var(--max-circle) - var(--circle-size)) / 2);
`;

const MiniButtonFig = styled.button`
  position: relative;
  width: var(--circle-size);
  height: var(--circle-size);
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  transition: transform 0.08s ease, filter 0.15s ease;

  &:active {
    transform: translateY(1px);
  }
  &:hover {
    filter: brightness(1.05);
  }
`;

/* 45x45 的描邊圓（Figma：Ellipse 30 / 1px white border） */
const MiniRing = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 1px solid #fff;
`;

/* 標籤字（與 UploadLabel 一致的白色漸層＋陰影） */
const MiniLabel = styled.div`
  width: 100%;
  text-align: center;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: clamp(12px, 3vw, 14px);
  line-height: 20px;

  background: linear-gradient(0deg, #ffffff, #ffffff),
    linear-gradient(0deg, rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0.32)),
    linear-gradient(
      89.61deg,
      rgba(255, 255, 255, 0) -20.99%,
      #ffffff 38.34%,
      rgba(255, 255, 255, 0) 100%
    );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0px 4px 10.4px rgba(0, 0, 0, 0.25);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    line-height: 1.2;
  }
`;

/* 右側：sparkles-sharp（20x20），不用絕對定位，置中即可 */
const SparklesIcon = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 44%;
  height: 44%;
  /* 白色實心（Figma 多個 Vector） */
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

/* 左側：返回箭頭（17x17）＋陰影 */
const BackIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  filter: drop-shadow(0px 4px 10.4px rgba(0, 0, 0, 0.25));
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;

/* ===== 使用範例對話框（Figma 對應 Group 1272628298） ===== */

const ExampleDialog = styled.div<{ open?: boolean }>`
  position: fixed;
  left: 50%;
  transform: translateX(-50%) translateY(${(p) => (p.open ? "0" : "8px")});
  bottom: calc(env(safe-area-inset-bottom, 0px) + var(--dock-stack, 10px));
  width: clamp(340px, 92vw, 377px);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.11);
  backdrop-filter: blur(10.25px);
  padding: 18px 14px 14px;
  z-index: 32;
  box-sizing: border-box;
  opacity: ${(p) => (p.open ? 1 : 0)};
  pointer-events: ${(p) => (p.open ? "auto" : "none")};
  transition: opacity 0.2s ease, transform 0.2s ease;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: clamp(700px, 88vw, 900px);
    padding: 26px 32px 32px;
  }
`;

/* 上方標題列 */
const DialogTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-bottom: 18px;
  }
`;

const DialogTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #fff;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    line-height: 1;
  }
`;

const DialogStarIcon = styled.img`
  width: 12px;
  height: 12px;
  display: block;

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 30px;
    height: 30px;
  }
`;

const DialogClose = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  color: #fff;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0.9;
  transition: opacity 0.2s ease;
  &:hover {
    opacity: 1;
  }
  svg {
    width: 12px;
    height: 12px;
    stroke-width: 1.5;
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    width: 44px;
    height: 44px;

    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

/* 範例 chips 列表 */
const DialogChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const DialogChip = styled.button`
  appearance: none;
  border: none;
  padding: 8px 14px;
  border-radius: 16.5px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-family: "Roboto", system-ui, sans-serif;
  font-size: 12px;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.08s ease;
  &:hover {
    filter: brightness(1.15);
  }
  &:active {
    transform: translateY(1px);
  }

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 24px;
    line-height: 1;
    padding: 20px 32px;
    border-radius: 36px;
  }
`;

/* 隱藏檔案 input，用 central button 觸發 */
const HiddenInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  overflow: hidden;
  border: 0;
  padding: 0;
  margin: -1px;
`;
const UploadHint = styled.div`
  text-align: center;
  font-family: "PingFang TC", system-ui, sans-serif;
  font-weight: 400;
  font-size: clamp(12px, 2.4vw, 11px);
  text-wrap: wrap;
  line-height: 1.4;
  width: 100%;
  opacity: 0.9;
  background: linear-gradient(0deg, #ffffff, #ffffff),
    linear-gradient(0deg, rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0.32)),
    linear-gradient(
      89.61deg,
      rgba(255, 255, 255, 0) -20.99%,
      #ffffff 38.34%,
      rgba(255, 255, 255, 0) 100%
    );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0px 4px 10.4px rgba(0, 0, 0, 0.39);

  /* 針對平板尺寸 */
  @media (min-width: 768px) and (max-width: 1024px) {
    font-size: 20px;
  }
`;

/* 內容底部留白，避免被固定輸入列遮住 */
const Spacer = styled.div`
  height: 120px; /* 視你的 ImageTextInput 高度微調 */
`;

/** 把圖片路徑轉成 Data URI（Base64） */
async function toDataUri(url: string): Promise<string> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Load image failed: ${url}`);
  const blob = await res.blob();
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(reader.result as string);
    reader.readAsDataURL(blob);
  });
}

/* ====== 頁面元件 ====== */
export default function ChatbotPage() {
  const { t, locale } = useLocalization();
  const navigate = useNavigate();
  // state
  const [panelOpen, setPanelOpen] = useState(false);
  const fileInputId = "upload-hidden-input";

  const suffix = locale === "zh" ? "zh" : "en";
  const buildExamplePath = (idx: number) =>
    `/images/example-${idx}-${suffix}.png`;

  /** 點擊範例 chip → 讀圖成 Base64 → 導到 /preview-image（透過 state 帶參數） */
  const goPreview = async (idx: number) => {
    const imgUrl = buildExamplePath(idx);
    const dataUrl = await toDataUri(imgUrl);
    navigate("/preview-image", {
      state: {
        dataUrl,
      },
    });
  };

  const triggerUpload = () => {
    const el = document.getElementById(fileInputId) as HTMLInputElement | null;
    el?.click();
  };

  const handleFile = async (file: File) => {
    if (!file) return;
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result));
      fr.onerror = () => reject(fr.error);
      fr.readAsDataURL(file);
    });
    navigate("/preview-image", { state: { dataUrl } });
  };

  // 1) 新增 ref
  const dockRef = React.useRef<HTMLDivElement | null>(null);

  // 2) 首次與尺寸變動時，更新 CSS 變數
  useEffect(() => {
    if (!dockRef.current) return;

    const setVar = () => {
      const h = dockRef.current?.offsetHeight ?? 0;
      // 38px = 你的 BottomDock: bottom: 38px;   12px = 想保留的間距（可調）
      document.documentElement.style.setProperty(
        "--dock-stack",
        `${38 + h + 12}px`
      );
    };

    setVar();

    // 監看尺寸變動（鍵盤彈起、i18n、文案換行等都能跟上）
    const ro = new ResizeObserver(setVar);
    ro.observe(dockRef.current);

    // 回收
    return () => ro.disconnect();
  }, []);

  return (
    <Page>
      {/* 標題（兩行） */}
      <TitleWrap>
        <TitleLine>{t("hello")}</TitleLine>
        <TitleLine>{t("check_suspicious_content")}</TitleLine>
        {/* 中央球體 */}
        <HeroWrap>
          <HaloAnimation />
        </HeroWrap>
      </TitleWrap>

      {/* 增加底部留白避免被固定輸入列遮住 */}
      <Spacer />

      {/* 固定在底部的輸入列（保留你的 ImageTextInput 元件） */}
      {/* 底部 Dock */}
      <BottomDock ref={dockRef}>
        <DockInner>
          {/* 左側：返回（Figma 45x45 + 17x17 內圖形） */}
          <MiniGroup>
            <MiniButtonFig
              onClick={() => navigate(-1)}
              aria-label={t("back") ?? "返回"}
            >
              <MiniRing />
              <BackIcon>
                <img
                  src="/images/arrow-icon.svg"
                  alt="Back arrow"
                  style={{
                    objectFit: "contain",
                    marginTop: "5px",
                  }}
                />
              </BackIcon>
            </MiniButtonFig>
            <MiniLabel>{t("back") ?? "返回"}</MiniLabel>
          </MiniGroup>

          {/* 中央：上傳 */}
          <CenterWrap>
            <UploadGroup>
              <UploadButtonFig
                onClick={triggerUpload}
                aria-label={t("upload_suspect") ?? "上傳可疑截圖"}
              >
                <ArrowUp>
                  {/* 以線段粗度 5px 畫上箭頭（對應 Vector 7/8） */}
                  <img
                    src="/images/arrow-up-icon.svg"
                    alt="up arrow"
                    style={{
                      objectFit: "contain",
                      marginTop: "3px",
                      marginLeft: "2px",
                    }}
                  />
                </ArrowUp>
                <RingInner />
                <RingMiddle />
                <RingOuter />
              </UploadButtonFig>

              <UploadLabel>{t("upload_suspect") ?? "上傳可疑截圖"}</UploadLabel>
              <UploadHint>
                {t("image_size_remark", { min: 10, max: 6 })}
              </UploadHint>

              {/* 隱藏 input，供按鈕觸發 */}
              <HiddenInput
                id={fileInputId}
                type="file"
                accept="image/*"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const f = e.currentTarget.files?.[0];
                  if (f) void handleFile(f);
                  e.currentTarget.value = "";
                }}
              />
            </UploadGroup>
          </CenterWrap>

          {/* 右側：使用範例（Figma 45x45 + sparkles + 漸層文字） */}
          <MiniGroup>
            <MiniButtonFig
              onClick={() => setPanelOpen(true)}
              aria-label={t("use_example") ?? "使用範例"}
            >
              <MiniRing />
              <SparklesIcon>
                {/* 以一顆主星＋兩顆小星模擬 Figma 多個 Vector 的白色塊狀 */}
                <img
                  src="/images/sparkles-logo.svg"
                  alt="sparkles"
                  style={{
                    objectFit: "contain",
                    width: "100%",
                    height: "100%",
                  }}
                />
              </SparklesIcon>
            </MiniButtonFig>
            <MiniLabel>{t("use_example") ?? "使用範例"}</MiniLabel>
          </MiniGroup>
        </DockInner>
      </BottomDock>

      {/* 使用範例對話框（比上傳按鈕高一層） */}
      <ExampleDialog open={panelOpen}>
        <DialogTitleRow>
          <DialogTitleLeft>
            <DialogStarIcon src="/images/sparkles-logo.svg" alt="icon" />
            <span>{t("select_example") ?? "請選擇範例"}</span>
          </DialogTitleLeft>
          <DialogClose onClick={() => setPanelOpen(false)} aria-label="close">
            <svg viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke="#FFFFFF" />
            </svg>
          </DialogClose>
        </DialogTitleRow>

        <DialogChipRow>
          <DialogChip
            onClick={() => {
              setPanelOpen(false);
              void goPreview(1);
            }}
          >
            {t("scam_example_1") ?? "投資詐騙"}
          </DialogChip>
          <DialogChip
            onClick={() => {
              setPanelOpen(false);
              void goPreview(2);
            }}
          >
            {t("scam_example_2") ?? "投資詐騙網址廣告"}
          </DialogChip>
          <DialogChip
            onClick={() => {
              setPanelOpen(false);
              void goPreview(3);
            }}
          >
            {t("scam_example_3") ?? "假冒政府詐騙訊息"}
          </DialogChip>
        </DialogChipRow>
      </ExampleDialog>
    </Page>
  );
}
